import { ACClient } from 'vue-infra';
import Config from '@config/config';
import ApiResources from '@core/data/apiResources';
import BaseClient from '@core/common/BaseClient';
import BaseService from '@core/common/BaseService';

const defaultPageSizes = {
  agentList: 25,
  botList: 25,
  skillList: 25,
  conversations: 50,
  in_queue: 50,
};

const acPropertiesMap = {
  PAGE_SIZE: 'le.managerWorkspace.ui.settings.pagination.pageSize',
};

/**
 * Prepares request body to save pagination page size into AC
 * @param pageSizes
 * @return {[{propertyValue: {value}, id: string, type: number}]}
 */
const createSaveRequestBody = pageSizes => [
  {
    id: acPropertiesMap.PAGE_SIZE,
    type: 4,
    propertyValue: {
      value: pageSizes,
    },
  },
];

class AcSettingsService extends BaseService {
  constructor() {
    super({ name: 'ACUserSettingsService' });
    this.intialized = false;
    this.resource = null;
    this.acClient = null;
    this.settings = {
      pagination: { pageSize: {} },
    };
  }

  initialize({ userId }) {
    if (!this.intialized) {
      this.resource = this.resolveResource(
        userId,
        ApiResources.AccountConfig.SiteSettingsUserProperties,
      );
      this.acClient = new ACClient({
        allowedMethods: ['get', 'post', 'patch'],
        path: '/',
        source: {
          appName: Config.moduleName,
          contextName: 'acUSet',
        },
        config: {
          timeout: 30000,
        },
        retryConfig: BaseClient.getClientRetryConfig,
      });
      this.initialized = true;
    }
    return this;
  }

  /**
   * Resolve resource using LE.urlResolver
   * @param userId
   * @param apiResource
   * @return {{search: string, pathname: *}}
   */
  // eslint-disable-next-line class-methods-use-this
  resolveResource(userId, apiResource) {
    const { getUrl, getUrlParts } = window.LE.urlResolver;
    const { url, version, params } = apiResource;
    const { pathname, search } = getUrlParts(getUrl(url, { userId }));
    const query = `${search}&${params.user.replace('@@user_id', userId)}&v=${version}`;
    return {
      pathname,
      search: query,
    };
  }

  // eslint-disable-next-line class-methods-use-this,no-unused-vars
  async loadPaginationPageSize() {
    const path = `${this.resource.pathname}/${acPropertiesMap.PAGE_SIZE}/${this.resource.search}`;
    try {
      const { data, status } = await this.acClient.get(path);
      if (status < 300) {
        this.settings.pagination = {
          pageSize: data.propertyValue.value,
        };
      } else if (data.code > 1000) {
        this.settings.pagination = {
          pageSize: defaultPageSizes,
        };
      }
    } catch (err) {
      this.logError(err.message);
      this.settings.pagination = {
        pageSize: defaultPageSizes,
      };
    }
  }

  /**
   * Saves page size to AC User settings
   * Due to limitations, must save all values at once
   * meaning that we cant save page size for one widget but have to rewrite all
   * stored at AC values.
   * @param pageSize
   * @param moduleName {String} - comes from widgetName parameter passed on pagination init at each
   * index.js store module. See agent-list/index.js store module for example
   */
  async savePaginationPageSize({ pageSize, moduleName }) {
    const path = `${this.resource.pathname}/${this.resource.search}`;
    const requestConfig = { headers: { 'If-Match': '-1' } }; // disregard revision,
    const body = createSaveRequestBody({
      ...this.settings.pagination.pageSize,
      [moduleName]: pageSize,
    });

    await this.acClient.patch(path, body, requestConfig);
  }

  getPaginationPageSize(moduleName) {
    return this.settings.pagination.pageSize[moduleName];
  }
}

export const ACUserSettings = new AcSettingsService();
